import { createContext, useCallback, useContext, useEffect, useRef, useState, type Dispatch, type ReactNode, type SetStateAction } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/modules/authentication/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { getHostJoinReminders } from '../api/api-meeting';
import { HOST_REMINDER_INTERVAL_MS, nextHostReminder, type HostJoinReminder } from '../utils/hostReminders';
import { toast } from 'sonner';

const MeetingPresenceContext = createContext<{
  activeMeetingCode: string | null;
  setActiveMeetingCode: Dispatch<SetStateAction<string | null>>;
}>({activeMeetingCode: null, setActiveMeetingCode: () => undefined});

export const useMeetingPresence = () => useContext(MeetingPresenceContext);

export function MeetingReminderProvider({children}: {children: ReactNode}) {
  const {user, isAuthenticated} = useAuth();
  const userId = isAuthenticated ? user?.id : undefined;
  const navigate = useNavigate();
  const [activeMeetingCode, setActiveMeetingCode] = useState<string | null>(null);
  const [meetings, setMeetings] = useState<HostJoinReminder[]>([]);
  const [checkedAt, setCheckedAt] = useState(0);
  const [dismissedUntil, setDismissedUntil] = useState<Record<string, number>>({});
  const [joining, setJoining] = useState(false);
  const refreshRef = useRef<() => Promise<HostJoinReminder[] | null>>(async () => null);
  const snoozeTimers = useRef(new Set<ReturnType<typeof setTimeout>>());

  useEffect(() => {
    setMeetings([]);
    setDismissedUntil({});
    setCheckedAt(0);
    setJoining(false);
    if (!userId) return;
    let disposed = false;
    const endedMeetings = new Set<string>();
    let pending: Promise<HostJoinReminder[] | null> | null = null;
    const controller = new AbortController();
    const refresh = () => {
      if (pending) return pending;
      pending = (async () => {
        try {
          const response = await getHostJoinReminders(controller.signal);
          if (disposed) return null;
          const items = response.data.meetings.filter(item => !endedMeetings.has(item.id));
          setMeetings(items);
          setCheckedAt(Date.now());
          return items;
        } catch {
          if (!disposed) setMeetings([]);
          return null;
        } finally { pending = null; }
      })();
      return pending;
    };
    refreshRef.current = refresh;
    void refresh(); // Includes an existing session restored at login.
    const poll = setInterval(() => { void refresh(); }, HOST_REMINDER_INTERVAL_MS);
    const onFocus = () => { if (document.visibilityState !== 'hidden') void refresh(); };
    const onEnded = (event: Event) => {
      const id = (event as CustomEvent<{id: string}>).detail?.id;
      if (!id) return;
      endedMeetings.add(id);
      setMeetings(current => current.filter(item => item.id !== id));
    };
    window.addEventListener('meeting-ended', onEnded);
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    window.addEventListener('meeting-preferences-changed', onFocus);
    return () => {
      disposed = true;
      controller.abort();
      clearInterval(poll);
      snoozeTimers.current.forEach(clearTimeout);
      snoozeTimers.current.clear();
      refreshRef.current = async () => null;
      window.removeEventListener('meeting-ended', onEnded);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
      window.removeEventListener('meeting-preferences-changed', onFocus);
    };
  }, [userId]);

  const snooze = useCallback((id: string) => {
    setDismissedUntil(current => ({...current, [id]: Date.now() + HOST_REMINDER_INTERVAL_MS}));
    const timer = setTimeout(() => {
      snoozeTimers.current.delete(timer);
      void refreshRef.current();
    }, HOST_REMINDER_INTERVAL_MS);
    snoozeTimers.current.add(timer);
  }, []);
  const invitation = userId ? nextHostReminder(meetings, activeMeetingCode, dismissedUntil, checkedAt) : undefined;

  const join = async () => {
    if (!invitation || joining) return;
    if (activeMeetingCode) {
      const target = `/meeting/${encodeURIComponent(invitation.meetingCode)}`;
      const href = import.meta.env.VITE_USE_HASH_ROUTE === 'true' ? `/#${target}` : target;
      // Open directly in the click handler so popup blockers do not reject it after an await.
      window.open(href, '_blank', 'noopener,noreferrer');
      snooze(invitation.id);
      return;
    }
    setJoining(true);
    // Recheck before navigating; an open invitation may have been cancelled elsewhere.
    const latest = await refreshRef.current();
    if (!latest) {
      toast.error('Chưa kiểm tra được cuộc họp. Vui lòng thử lại.');
      setJoining(false);
      return;
    }
    if (latest.some(item => item.id === invitation.id)) {
      snooze(invitation.id);
      const target = `/meeting/${encodeURIComponent(invitation.meetingCode)}`;
      navigate(target); // The meeting route remounts even after an earlier failed join.
    }
    setJoining(false);
  };

  return <MeetingPresenceContext.Provider value={{activeMeetingCode, setActiveMeetingCode}}>
    {children}
    <Dialog open={!!invitation} onOpenChange={open => { if (!open && invitation && !joining) snooze(invitation.id); }}>
      <DialogContent className="sm:max-w-lg" onPointerDownOutside={event => event.preventDefault()}>
        <DialogHeader>
          <DialogTitle>Đã đến giờ họp, mời bạn vào họp</DialogTitle>
          <DialogDescription>
            Bạn là người tạo hoặc chủ trì cuộc họp. Nếu từ chối, lời mời sẽ xuất hiện lại sau 30 giây khi bạn vẫn chưa vào họp.
          </DialogDescription>
        </DialogHeader>
        <div className="rounded-md border p-3 space-y-1">
          <p className="font-semibold break-words">{invitation?.title}</p>
          <p className="text-sm text-muted-foreground">Mã cuộc họp: {invitation?.meetingCode}</p>
        </div>
        {activeMeetingCode && <p className="text-sm">Bạn đang ở cuộc họp khác. Cuộc họp này sẽ mở trong tab mới.</p>}
        <DialogFooter className="gap-2">
          <Button type="button" variant="outline" disabled={joining} onClick={()=>invitation && snooze(invitation.id)}>Từ chối · Nhắc lại sau 30 giây</Button>
          <Button type="button" disabled={joining} onClick={()=>void join()}>{joining ? 'Đang kiểm tra…' : activeMeetingCode ? 'Vào họp trong tab mới' : 'Vào họp'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </MeetingPresenceContext.Provider>;
}
